import React, { useState, useEffect, useMemo, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useParams } from "react-router-dom";
import Swal from "sweetalert2";
import Breadcrums from "../../../common/breadcrumbs";
import { STATUS, SWAL_SETTINGS, THEME_COLORS, VERIFICATION_STATUS } from "../../../../../utils/Constants";
import {
  TrimText,
  handleServerValidations,
  hasP,
} from "../../../../../utils/commonfunction";
import * as clubAdmin from "../../../services/clubAdmin.service";
import { ExportUser, ExportUserPDF } from "../../../services/usermgmt.services";
import * as globalSettingsService from "../../../services/globalsetting.services";
import { useTranslation } from "react-i18next";
import { CustomProvider, DateRangePicker, Dropdown, Placeholder, Tooltip, Whisper } from "rsuite";
import { useQuery } from "react-query";
import { AgGridReact } from "ag-grid-react";
import { AllCommunityModule, ModuleRegistry } from "ag-grid-community";
import moment from "moment";
import NoRowsOverlay from "../../../common/NoRowsOverlay";
import ActionCell from "../../../shared/ActionCell";

ModuleRegistry.registerModules([AllCommunityModule]);

const ClubAdminList = () => {
  const params = useParams();
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const gridRef = useRef();

  const [searchData, setSearchData] = useState("");
  const [statsupdate, setStatusUpdate] = useState("false");
  const [pageIndex] = useState(Number(params.pgno) - 1 || 0);
  const [perPage, setPerPage] = useState(10);
  const [activeTab, setActiveTab] = useState("verified");
  const [selectedRows, setSelectedRows] = useState([]);

  const breadcrumbs = [
    { title: t("sidebar_link_dashboard"), url: "/admin/dashboard" },
    { title: t("Club"), url: "" },
  ];

  // ================= FETCH GLOBAL SETTINGS =================
  const { data: globalSettingsRes } = useQuery({
    queryKey: ["global-settings-details"],
    queryFn: () => globalSettingsService.Details(),
    refetchOnWindowFocus: false,
    retry: false,
  });

  const globalSettings = globalSettingsRes?.data || {};
  const standardFreeDays = globalSettings.standard_free_days !== undefined ? Number(globalSettings.standard_free_days) : 30;
  const partnerFreeDays = globalSettings.partner_free_days !== undefined ? Number(globalSettings.partner_free_days) : 60;
  const founderFreeDays = globalSettings.founder_free_days !== undefined ? Number(globalSettings.founder_free_days) : 60;

  // ================= FETCH ALL DATA =================
  const { data, isLoading, refetch } = useQuery({
    queryKey: ["club-admin-list"],
    queryFn: () => clubAdmin.List({
      limit: 1000,
      lastKey: null,
      filters: {},
      sorting: {}
    }),
    refetchOnWindowFocus: false,
    retry: false,
    onError: (error) => {
      Swal.fire({
        icon: "error",
        text: handleServerValidations(error),
        ...SWAL_SETTINGS,
      });
    },
  });

  // ================= ACTIONS =================
  const viewfunction = (row) => {
    navigate(`/admin/user-management/club-admins/view/${row.originalId}`);
  };

  const Editfunction = (row) => {
    navigate(`/admin/user-management/club-admins/${params.pgno}/edit/${row.originalId}`);
  };

  const downloadPDF = async (row) => {
    try {
      const response = await ExportUserPDF(row.originalId);
      const blob = response instanceof Blob ? response : response?.data;
      if (blob instanceof Blob) {
        let fileName = "";
        const contentDisposition = response?.headers?.["content-disposition"];
        if (contentDisposition) {
          const match = contentDisposition.match(/filename="?([^";]+)"?/);
          if (match && match[1]) {
            fileName = match[1];
          }
        }
        if (!fileName) {
          const surnameOrName = row.surname || row.lastName || row.username || row.firstName || row.name || row.originalId;
          const safeSurname = String(surnameOrName).trim().replace(/[^a-zA-Z0-9_-]/g, "_");
          fileName = `Club_Admin_Report_${safeSurname}.pdf`;
        }

        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } else {
        Swal.fire({ icon: "error", text: "Failed to download PDF. Please try again.", ...SWAL_SETTINGS });
      }
    } catch (err) {
      Swal.fire({ icon: "error", text: handleServerValidations(err), ...SWAL_SETTINGS });
    }
  };

  // ================= STATUS - INSTANT UPDATE + API CALL =================
  const ChangeStatus = (id, newStatus) => {
    const formData = new FormData();
    formData.append("status", newStatus);

    Swal.fire({
      customClass: "swal-wide",
      title: t("msg_are_you_sure"),
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: THEME_COLORS.ADMIN_PRIMARY,
      cancelButtonColor: "#000",
      confirmButtonText: t("btn_yes"),
      cancelButtonText: t("btn_cancel")
    }).then((result) => {
      if (result.isConfirmed) {
        // 🔥 Optimistic UI Update
        if (gridRef.current?.api) {
          const rowNode = gridRef.current.api.getRowNode(String(id));
          if (rowNode) {
            rowNode.setDataValue("status", newStatus);
          }
        }

        // API call
        clubAdmin.Status(formData, id).then((response) => {
          if (response.success) {
            Swal.fire({
              icon: "success",
              text: response.message,
              ...SWAL_SETTINGS,
            });
          } else {
            // Revert
            if (gridRef.current?.api) {
              const rowNode = gridRef.current.api.getRowNode(String(id));
              if (rowNode) {
                rowNode.setDataValue(
                  "status",
                  newStatus === STATUS.ACTIVE ? STATUS.INACTIVE : STATUS.ACTIVE
                );
              }
            }
            Swal.fire({
              icon: "error",
              text: handleServerValidations(response),
              ...SWAL_SETTINGS,
            });
          }
        }).catch((error) => {
          // Revert
          if (gridRef.current?.api) {
            const rowNode = gridRef.current.api.getRowNode(String(id));
            if (rowNode) {
              rowNode.setDataValue(
                "status",
                newStatus === STATUS.ACTIVE ? STATUS.INACTIVE : STATUS.ACTIVE
              );
            }
          }
          Swal.fire({
            icon: "error",
            text: handleServerValidations(error),
            ...SWAL_SETTINGS,
          });
        });
      }
    });
  };

  // 🔥 FIXED DELETE FUNCTION - S.No Issue Solved
  function Deletefunction(data) {
    Swal.fire({
      customClass: "swal-wide",
      title: t("msg_are_you_sure"),
      text: t("you_not_be_able_to_revert_this"),
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: THEME_COLORS.ADMIN_PRIMARY,
      cancelButtonColor: "#000",
      confirmButtonText: t("btn_yes_delete"),
      cancelButtonText: t("btn_cancel")
    }).then((result) => {
      if (result.isConfirmed) {
        clubAdmin.Delete(data.originalId).then((response) => {
          if (response.success) {
            Swal.fire({
              icon: "success",
              text: response.message,
              ...SWAL_SETTINGS,
            });
            // ✅ FIXED: Use refetch to recalculate S.No
            refetch();
          } else {
            Swal.fire({
              icon: "error",
              text: handleServerValidations(response),
              ...SWAL_SETTINGS,
            });
          }
        }).catch((error) => {
          Swal.fire({
            icon: "error",
            text: handleServerValidations(error),
            ...SWAL_SETTINGS,
          });
        });
      }
    });
  }

  const onSelectionChanged = (event) => {
    if (event && event.api) {
      const selectedNodes = event.api.getSelectedNodes();
      const selectedData = selectedNodes.map((node) => node.data);
      setSelectedRows(selectedData);
    }
  };

  const handleDeleteMultiple = () => {
    if (selectedRows.length === 0) {
      Swal.fire({
        icon: "warning",
        text: "Please select at least one record to delete.",
        ...SWAL_SETTINGS,
      });
      return;
    }

    Swal.fire({
      customClass: "swal-wide",
      title: t("msg_are_you_sure"),
      text: `Are you sure you want to delete ${selectedRows.length} selected record(s)? This action cannot be undone.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: THEME_COLORS.ADMIN_PRIMARY,
      cancelButtonColor: "#000",
      confirmButtonText: t("btn_yes_delete"),
      cancelButtonText: t("btn_cancel")
    }).then((result) => {
      if (result.isConfirmed) {
        Swal.fire({
          title: "Deleting...",
          text: "Please wait while selected records are being deleted",
          allowOutsideClick: false,
          didOpen: () => {
            Swal.showLoading();
          }
        });

        const deletePromises = selectedRows.map((row) => clubAdmin.Delete(row.originalId));
        Promise.all(deletePromises)
          .then((results) => {
            setSelectedRows([]);
            if (gridRef.current && gridRef.current.api) {
              gridRef.current.api.deselectAll();
            }
            Swal.fire({
              icon: "success",
              title: "Deleted!",
              text: `${results.length} record(s) deleted successfully.`,
              ...SWAL_SETTINGS,
            });
            refetch();
          })
          .catch((error) => {
            Swal.fire({
              icon: "error",
              text: handleServerValidations(error),
              ...SWAL_SETTINGS,
            });
            refetch();
          });
      }
    });
  };

  function changeClubTypeFunction(data, clubType) {
    clubAdmin.changeClubType({ clubType }, data.originalId).then((response) => {
      if (response.success) {
        Swal.fire({
          icon: "success",
          text: response.message,
          ...SWAL_SETTINGS,
        });
        refetch();
      } else {
        Swal.fire({
          icon: "error",
          text: handleServerValidations(response),
          ...SWAL_SETTINGS,
        });
      }
    }).catch((error) => {
      Swal.fire({
        icon: "error",
        text: handleServerValidations(error),
        ...SWAL_SETTINGS,
      });
    });
  }

  function Verifyfunction(data, verifyStatus) {
    const isVerifying = verifyStatus === "verified" || verifyStatus === true || verifyStatus === "true";
    const htmlContent = isVerifying ? `
      <div class="text-center mt-2">
        <p class="text-muted text-center mb-0">${t("you_not_be_able_to_revert_this")}</p>
      </div>
    ` : `
      <div class="text-start mt-2">
        <p class="text-muted text-center mb-3">${t("you_not_be_able_to_revert_this")}</p>
        <div class="form-group text-start">
          <label for="swal-reason-textarea" class="form-label font-weight-bold mb-1 text-dark" style="font-size: 14px; font-weight: 600;">
            ${t("Reason")}
          </label>
          <textarea
            id="swal-reason-textarea"
            class="form-control"
            rows="3"
            placeholder="${t("Write reason here...")}"
            style="font-size: 14px; border: 1px solid #ced4da; border-radius: 6px; padding: 8px 12px;"
          ></textarea>
        </div>
      </div>
    `;

    Swal.fire({
      customClass: "swal-wide",
      title: t("msg_are_you_sure"),
      html: htmlContent,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: THEME_COLORS.ADMIN_PRIMARY,
      cancelButtonColor: "#000",
      confirmButtonText: t("btn_yes"),
      cancelButtonText: t("btn_cancel"),
      preConfirm: () => {
        if (isVerifying) return "";
        const textarea = document.getElementById("swal-reason-textarea");
        return textarea ? textarea.value.trim() : "";
      }
    }).then((result) => {
      if (result.isConfirmed) {
        const reason = result.value || "";
        const formdata = new FormData();
        let id = [data.originalId];
        formdata.append("isProfileVerified", verifyStatus);
        formdata.append("reason", reason);
        clubAdmin.profileVerify(formdata, id).then((response) => {
          if (response.success) {
            Swal.fire({
              icon: "success",
              text: response.message,
              ...SWAL_SETTINGS,
            });
            refetch();
          } else {
            Swal.fire({
              icon: "error",
              text: handleServerValidations(response),
              ...SWAL_SETTINGS,
            });
          }
        }).catch((error) => {
          Swal.fire({
            icon: "error",
            text: handleServerValidations(error),
            ...SWAL_SETTINGS,
          });
        });
      }
    });
  }

  function Blockfunction(data, blockStatus) {
    Swal.fire({
      customClass: "swal-wide",
      title: t("msg_are_you_sure"),
      text: t("you_not_be_able_to_revert_this"),
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: THEME_COLORS.ADMIN_PRIMARY,
      cancelButtonColor: "#000",
      confirmButtonText: t("btn_yes"),
      cancelButtonText: t("btn_cancel")
    }).then((result) => {
      if (result.isConfirmed) {
        const formdata = new FormData();
        formdata.append("isBlocked", blockStatus);
        clubAdmin.blockUser(formdata, data.originalId).then((response) => {
          if (response.success) {
            Swal.fire({
              icon: "success",
              text: response.message,
              ...SWAL_SETTINGS,
            });
            refetch();
          } else {
            Swal.fire({
              icon: "error",
              text: handleServerValidations(response),
              ...SWAL_SETTINGS,
            });
          }
        }).catch((error) => {
          Swal.fire({
            icon: "error",
            text: handleServerValidations(error),
            ...SWAL_SETTINGS,
          });
        });
      }
    });
  }

  async function ResendInvite(data) {
    const currentTemplate = data.clubType || "Founder";
    const clubName = data.name || data.firstName || "Club Admin";
    // Resolve email: prefer club-specific email, fallback to login email
    const clubEmail = data.clubDetails?.email || data.clubEmail || data.email || "N/A";
    const adminEmailOriginal = data.email || "N/A";

    Swal.fire({
      customClass: "swal-wide",
      title: "<span style='color: #007D7F; font-weight: 700;'>Send Invitation</span>",
      html: `
        <div style="text-align: left; margin-top: 10px;">
          <p style="font-size: 14px; color: #333; margin-bottom: 14px;">
            Send Customer Agreement onboarding invitation link to <strong>${clubName}</strong>?
          </p>
          <div style="background-color: #e6f2f2; border-left: 4px solid #007d7f; padding: 12px 14px; border-radius: 6px; margin-bottom: 14px;">
            <p style="font-size: 12px; color: #007d7f; margin: 0 0 6px 0; line-height: 1.4; font-weight: 600;">
              📄 ${currentTemplate} Tier Commercial Agreement Onboarding
            </p>
            <p style="font-size: 11px; color: #555; margin: 0 0 8px 0; line-height: 1.4;">
              This will generate and email a fresh onboarding link for the <strong>${currentTemplate}</strong> tier agreement to the following email address(es):
            </p>
            <div style="font-size: 12px; color: #222; background: #ffffff; border: 1px solid #bce2e0; padding: 8px 12px; border-radius: 6px;">
              <div><strong>Club Email:</strong> ${clubEmail}</div>
              ${clubEmail !== adminEmailOriginal && adminEmailOriginal !== "N/A" ? `<div style="margin-top:4px;"><strong>Admin Email:</strong> ${adminEmailOriginal}</div>` : ""}
            </div>
          </div>
          <p style="font-size: 11px; color: #888; margin: 0;">Sending the invitation will generate a fresh onboarding link and email it to the recipient(s).</p>
        </div>
      `,
      showCancelButton: true,
      confirmButtonColor: THEME_COLORS.ADMIN_PRIMARY || "#007D7F",
      cancelButtonColor: "#000",
      confirmButtonText: "<i class='fa fa-paper-plane me-1'></i> Send Invitation",
      cancelButtonText: t("btn_cancel"),
      preConfirm: () => {
        return { agreementType: currentTemplate };
      }
    }).then((result) => {
      if (result.isConfirmed && result.value) {
        clubAdmin.resendOnboardInvite(data.originalId, result.value).then((response) => {
          if (response.success) {
            Swal.fire({
              icon: "success",
              text: response.message || `Onboarding invitation sent successfully!`,
              ...SWAL_SETTINGS,
            });
            refetch();
          } else {
            Swal.fire({
              icon: "error",
              text: handleServerValidations(response),
              ...SWAL_SETTINGS,
            });
          }
        }).catch((error) => {
          let errorMsg = handleServerValidations(error);
          let type = "error";
          if (error?.response?.data?.message === "Account is already set up.") {
            type = "info";
            errorMsg = "Account is already set up.";
          }
          Swal.fire({
            icon: type,
            text: errorMsg,
            ...SWAL_SETTINGS,
          });
        });
      }
    });
  }


  function handleAutoPaymentDeduct(data) {
    Swal.fire({
      customClass: "swal-wide",
      title: "Auto Payment Deduct?",
      text: "Are you sure you want to trigger auto payment deduct for all players in this club?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: THEME_COLORS.ADMIN_PRIMARY,
      cancelButtonColor: "#000",
      confirmButtonText: t("btn_yes"),
      cancelButtonText: t("btn_cancel")
    }).then((result) => {
      if (result.isConfirmed) {
        clubAdmin.autoPaymentDeduct(data.originalId).then((response) => {
          if (response.success) {
            Swal.fire({
              icon: "success",
              text: response.message,
              ...SWAL_SETTINGS,
            });
          } else {
            Swal.fire({
              icon: "error",
              text: handleServerValidations(response),
              ...SWAL_SETTINGS,
            });
          }
        }).catch((error) => {
          Swal.fire({
            icon: "error",
            text: handleServerValidations(error),
            ...SWAL_SETTINGS,
          });
        });
      }
    });
  }

  function ExtendTrial(data) {
    // Determine the earliest month that is NOT already covered by the current free trial.
    // billingAnchorDate = 1st of the billing start month (i.e. free trial covers UP TO that date).
    // So if anchor = Aug 1 2026, the free trial covers everything before it (up to July 31).
    // The anchor month itself begins billing (August is the first billed month).
    // The first month we can extend the trial to cover is the anchor month itself.
    const now = new Date();

    let firstAvailableYear, firstAvailableMonth; // 0-indexed month
    if (data.billingAnchorDate) {
      const anchor = new Date(data.billingAnchorDate);
      firstAvailableYear = anchor.getFullYear();
      firstAvailableMonth = anchor.getMonth(); // start from the anchor month itself
    } else {
      // No anchor yet — compute the nominal free trial end from joinedAt + freeDays,
      // exactly the same way the tier cell renderer does it.
      const joinedAt = data.joinedAt || data.createdAt;
      let freeDays = 0;
      const clubType = data.clubType;
      if (clubType === "Partner") freeDays = partnerFreeDays;
      else if (clubType === "Founder") freeDays = founderFreeDays;
      else freeDays = standardFreeDays; // Standard or unknown

      if (joinedAt && freeDays > 0) {
        const joinedDate = new Date(joinedAt);
        const totalFreeDays = freeDays + (data.extraFreeDays || 0);
        const nominalEndDate = new Date(joinedDate.getTime() + totalFreeDays * 24 * 60 * 60 * 1000);
        // Round up to the 1st of the following month (that's the billing anchor)
        let anchorMonth = nominalEndDate.getMonth() + 1;
        let anchorYear = nominalEndDate.getFullYear();
        if (anchorMonth > 11) {
          anchorMonth = 0;
          anchorYear += 1;
        }
        firstAvailableYear = anchorYear;
        firstAvailableMonth = anchorMonth; // 0-indexed, this is the anchor month (first billed month)
      } else {
        // Fallback: no joinedAt or freeDays — start from current month
        firstAvailableYear = now.getFullYear();
        firstAvailableMonth = now.getMonth();
      }
    }

    // Build 12 months of options starting from firstAvailable
    const MONTH_NAMES = ["January","February","March","April","May","June","July","August","September","October","November","December"];
    const options = {};
    let yr = firstAvailableYear;
    let mo = firstAvailableMonth; // 0-indexed
    for (let i = 0; i < 12; i++) {
      // If we extend the trial to cover 'mo' month, the new billing anchor date
      // will be the 1st of the month AFTER 'mo'.
      let nextMo = mo + 1;
      let nextYr = yr;
      if (nextMo > 11) {
        nextMo = 0;
        nextYr += 1;
      }
      const key = `${nextYr}-${String(nextMo + 1).padStart(2, "0")}`; // e.g. "2026-10" if mo is 8 (September)
      options[key] = `${MONTH_NAMES[mo]} ${yr}`;
      mo++;
      if (mo > 11) { mo = 0; yr++; }
    }

    if (Object.keys(options).length === 0) {
      Swal.fire({ icon: "info", text: "No available months to extend the free trial.", ...SWAL_SETTINGS });
      return;
    }

    Swal.fire({
      title: "Extend Free Trial",
      text: "Select the month until which the free trial should be extended:",
      input: "select",
      inputOptions: options,
      inputPlaceholder: "-- Select a month --",
      showCancelButton: true,
      confirmButtonColor: THEME_COLORS.ADMIN_PRIMARY,
      cancelButtonColor: "#000",
      confirmButtonText: "Extend",
      cancelButtonText: t("btn_cancel"),
      preConfirm: (value) => {
        if (!value) {
          Swal.showValidationMessage("Please select a month.");
        }
        return value;
      }
    }).then((result) => {
      if (result.isConfirmed) {
        // value is "YYYY-MM", e.g. "2026-09"
        const [targetYear, targetMonth] = result.value.split("-").map(Number);
        clubAdmin.extendTrial({ targetYear, targetMonth }, data.originalId).then((response) => {
          if (response.success) {
            Swal.fire({
              icon: "success",
              text: response.message,
              ...SWAL_SETTINGS,
            });
            refetch();
          } else {
            Swal.fire({
              icon: "error",
              text: handleServerValidations(response),
              ...SWAL_SETTINGS,
            });
          }
        }).catch((error) => {
          Swal.fire({
            icon: "error",
            text: handleServerValidations(error),
            ...SWAL_SETTINGS,
          });
        });
      }
    });
  }

  function transferRoleFunction(row) {
    const countries = [
      "Australia", "United Kingdom", "United States", "New Zealand", "Canada", "South Africa",
      "Afghanistan", "Albania", "Algeria", "Andorra", "Angola", "Antigua and Barbuda", "Argentina",
      "Armenia", "Austria", "Azerbaijan", "Bahamas", "Bahrain", "Bangladesh", "Barbados", "Belarus",
      "Belgium", "Belize", "Benin", "Bhutan", "Bolivia", "Bosnia and Herzegovina", "Botswana",
      "Brazil", "Brunei", "Bulgaria", "Burkina Faso", "Burundi", "Cambodia", "Cameroon", "Cape Verde",
      "Central African Republic", "Chad", "Chile", "China", "Colombia", "Comoros", "Congo",
      "Costa Rica", "Croatia", "Cuba", "Cyprus", "Czech Republic", "Denmark", "Djibouti", "Dominica",
      "Dominican Republic", "East Timor", "Ecuador", "Egypt", "El Salvador", "Equatorial Guinea",
      "Eritrea", "Estonia", "Eswatini", "Ethiopia", "Fiji", "Finland", "France", "Gabon", "Gambia",
      "Georgia", "Germany", "Ghana", "Greece", "Grenada", "Guatemala", "Guinea", "Guinea-Bissau",
      "Guyana", "Haiti", "Honduras", "Hungary", "Iceland", "India", "Indonesia", "Iran", "Iraq",
      "Ireland", "Israel", "Italy", "Ivory Coast", "Jamaica", "Japan", "Jordan", "Kazakhstan",
      "Kenya", "Kiribati", "Korea, North", "Korea, South", "Kosovo", "Kuwait", "Kyrgyzstan", "Laos",
      "Latvia", "Lebanon", "Lesotho", "Liberia", "Libya", "Liechtenstein", "Lithuania", "Luxembourg",
      "Madagascar", "Malawi", "Malaysia", "Maldives", "Mali", "Malta", "Marshall Islands",
      "Mauritania", "Mauritius", "Mexico", "Micronesia", "Moldova", "Monaco", "Mongolia",
      "Montenegro", "Morocco", "Mozambique", "Myanmar", "Namibia", "Nauru", "Nepal", "Netherlands",
      "Nicaragua", "Niger", "Nigeria", "North Macedonia", "Norway", "Oman", "Pakistan", "Palau",
      "Palestine", "Panama", "Papua New Guinea", "Paraguay", "Peru", "Philippines", "Poland",
      "Portugal", "Qatar", "Romania", "Russia", "Rwanda", "Saint Kitts and Nevis", "Saint Lucia",
      "Saint Vincent and the Grenadines", "Samoa", "San Marino", "Sao Tome and Principe",
      "Saudi Arabia", "Senegal", "Serbia", "Seychelles", "Sierra Leone", "Singapore", "Slovakia",
      "Slovenia", "Solomon Islands", "Somalia", "Spain", "Sri Lanka", "Sudan", "Suriname",
      "Sweden", "Switzerland", "Syria", "Taiwan", "Tajikistan", "Tanzania", "Thailand", "Togo",
      "Tonga", "Trinidad and Tobago", "Tunisia", "Turkey", "Turkmenistan", "Tuvalu", "Uganda",
      "Ukraine", "United Arab Emirates", "Uruguay", "Uzbekistan", "Vanuatu", "Vatican City",
      "Venezuela", "Vietnam", "Yemen", "Zambia", "Zimbabwe"
    ];

    const countryOptions = countries.map(c => `<option value="${c}">${c}</option>`).join("");

    Swal.fire({
      customClass: "swal-wide",
      title: "<span style='color: #007D7F; font-weight: 700;'>Transfer Role</span>",
      html: `
        <div style="text-align: left; margin-top: 10px; max-height: 68vh; overflow-y: auto; padding-right: 5px;">
          <p style="font-size: 13px; color: #555; margin-bottom: 14px;">
            Transfer account role for <strong>${row.name || "this club admin"}</strong> to a new user. Enter the new account details below. Both old and new email holders will be notified.
          </p>

          <div style="margin-bottom: 10px;">
            <label style="font-weight:600; font-size:12px; color:#333; display:block; margin-bottom:4px;">Current Email</label>
            <input
              type="email"
              value="${row.email || ""}"
              disabled
              style="width:100%; padding:8px 12px; border-radius:6px; border:1px solid #ced4da; background:#f8f9fa; font-size:13px; box-sizing:border-box; color:#888;"
            />
          </div>

          <div style="margin-bottom: 10px;">
            <label style="font-weight:600; font-size:12px; color:#333; display:block; margin-bottom:4px;">New Email <span style="color:red">*</span></label>
            <input
              id="swal-transfer-email"
              type="email"
              placeholder="Enter new email address"
              style="width:100%; padding:8px 12px; border-radius:6px; border:1px solid #007D7F; font-size:13px; box-sizing:border-box;"
            />
          </div>

          <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-bottom: 10px;">
            <div>
              <label style="font-weight:600; font-size:12px; color:#333; display:block; margin-bottom:4px;">First Name</label>
              <input
                id="swal-transfer-firstname"
                type="text"
                value=""
                placeholder="First name"
                style="width:100%; padding:8px 12px; border-radius:6px; border:1px solid #ced4da; font-size:13px; box-sizing:border-box;"
              />
            </div>
            <div>
              <label style="font-weight:600; font-size:12px; color:#333; display:block; margin-bottom:4px;">Last Name</label>
              <input
                id="swal-transfer-lastname"
                type="text"
                value=""
                placeholder="Last name"
                style="width:100%; padding:8px 12px; border-radius:6px; border:1px solid #ced4da; font-size:13px; box-sizing:border-box;"
              />
            </div>
          </div>

          <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-bottom: 10px;">
            <div>
              <label style="font-weight:600; font-size:12px; color:#333; display:block; margin-bottom:4px;">Username</label>
              <input
                id="swal-transfer-username"
                type="text"
                value=""
                placeholder="Username"
                style="width:100%; padding:8px 12px; border-radius:6px; border:1px solid #ced4da; font-size:13px; box-sizing:border-box;"
              />
            </div>
            <div>
              <label style="font-weight:600; font-size:12px; color:#333; display:block; margin-bottom:4px;">Date of Birth (DOB)</label>
              <input
                id="swal-transfer-dob"
                type="date"
                value=""
                style="width:100%; padding:8px 12px; border-radius:6px; border:1px solid #ced4da; font-size:13px; box-sizing:border-box;"
              />
            </div>
          </div>

          <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-bottom: 10px;">
            <div>
              <label style="font-weight:600; font-size:12px; color:#333; display:block; margin-bottom:4px;">City</label>
              <input
                id="swal-transfer-city"
                type="text"
                value=""
                placeholder="City"
                style="width:100%; padding:8px 12px; border-radius:6px; border:1px solid #ced4da; font-size:13px; box-sizing:border-box;"
              />
            </div>
            <div>
              <label style="font-weight:600; font-size:12px; color:#333; display:block; margin-bottom:4px;">State / County</label>
              <input
                id="swal-transfer-state"
                type="text"
                value=""
                placeholder="State/County"
                style="width:100%; padding:8px 12px; border-radius:6px; border:1px solid #ced4da; font-size:13px; box-sizing:border-box;"
              />
            </div>
          </div>

          <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-bottom: 10px;">
            <div>
              <label style="font-weight:600; font-size:12px; color:#333; display:block; margin-bottom:4px;">Zip / Postal Code</label>
              <input
                id="swal-transfer-zipcode"
                type="text"
                value=""
                placeholder="Zip / Postal Code"
                style="width:100%; padding:8px 12px; border-radius:6px; border:1px solid #ced4da; font-size:13px; box-sizing:border-box;"
              />
            </div>
            <div>
              <label style="font-weight:600; font-size:12px; color:#333; display:block; margin-bottom:4px;">Country</label>
              <select
                id="swal-transfer-country"
                style="width:100%; padding:8px 12px; border-radius:6px; border:1px solid #ced4da; font-size:13px; box-sizing:border-box; background:#fff;"
              >
                <option value="">Select Country</option>
                ${countryOptions}
              </select>
            </div>
          </div>

          <div style="background:#fff3cd; border-left:4px solid #ffc107; padding:10px 14px; border-radius:6px; margin-top:12px;">
            <p style="font-size:11px; color:#856404; margin:0; line-height:1.5;">
              ⚠️ This will update the login email and user profile details, generate a new temporary password, and notify both parties via email.
            </p>
          </div>
        </div>
      `,
      showCancelButton: true,
      confirmButtonColor: "#007D7F",
      cancelButtonColor: "#000",
      confirmButtonText: "<i class='ri-arrow-left-right-line me-1'></i> Transfer",
      cancelButtonText: t("btn_cancel"),
      preConfirm: () => {
        const emailInput = document.getElementById("swal-transfer-email");
        const firstNameInput = document.getElementById("swal-transfer-firstname");
        const lastNameInput = document.getElementById("swal-transfer-lastname");
        const usernameInput = document.getElementById("swal-transfer-username");
        const dobInput = document.getElementById("swal-transfer-dob");
        const cityInput = document.getElementById("swal-transfer-city");
        const stateInput = document.getElementById("swal-transfer-state");
        const zipCodeInput = document.getElementById("swal-transfer-zipcode");
        const countryInput = document.getElementById("swal-transfer-country");

        const newEmail = emailInput ? emailInput.value.trim() : "";
        if (!newEmail) {
          Swal.showValidationMessage("New email is required.");
          return false;
        }
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(newEmail)) {
          Swal.showValidationMessage("Please enter a valid email address.");
          return false;
        }
        if (newEmail.toLowerCase() === (row.email || "").toLowerCase()) {
          Swal.showValidationMessage("New email must be different from the current email.");
          return false;
        }

        return {
          newEmail,
          firstName: firstNameInput ? firstNameInput.value.trim() : "",
          lastName: lastNameInput ? lastNameInput.value.trim() : "",
          username: usernameInput ? usernameInput.value.trim() : "",
          dob: dobInput ? dobInput.value.trim() : "",
          city: cityInput ? cityInput.value.trim() : "",
          state: stateInput ? stateInput.value.trim() : "",
          zipCode: zipCodeInput ? zipCodeInput.value.trim() : "",
          country: countryInput ? countryInput.value.trim() : ""
        };
      }
    }).then((result) => {
      if (result.isConfirmed && result.value) {
        clubAdmin.transferRole(row.originalId, result.value).then((response) => {
          if (response.success) {
            Swal.fire({
              icon: "success",
              text: response.message || "Role transferred successfully!",
              ...SWAL_SETTINGS,
            });
            refetch();
          } else {
            Swal.fire({
              icon: "error",
              text: handleServerValidations(response),
              ...SWAL_SETTINGS,
            });
          }
        }).catch((error) => {
          Swal.fire({
            icon: "error",
            text: handleServerValidations(error),
            ...SWAL_SETTINGS,
          });
        });
      }
    });
  }

  const renderProfileStatus = (row) => {
    if (row.isBlocked) {
      return <span className="badge badge-pill badge-danger-light fw-bold text-uppercase">Blocked</span>;
    }
    switch (row.isProfileVerified) {
      case "verified":
        return <span className="badge badge-pill badge-success-light fw-bold text-uppercase">{t("label_verified")}</span>;
      case "rejected":
        return <span className="badge badge-pill badge-danger-light fw-bold text-uppercase">{t("label_rejected")}</span>;
      default:
        return <span className="badge badge-pill badge-warning-light fw-bold text-uppercase">{t("label_pending")}</span>;
    }
  };

  const [actionState, setActionState] = useState({
    id: null,
    shouldVisible: false,
    positions: { top: 0, right: 0 },
    rowData: {}
  });

  const [actionOptions, setActionOptions] = useState([
    {
      label: "Reject",
      value: "rejected",
      metadata: {},
      onClick: Verifyfunction
    },
    {
      label: "Verify",
      value: "verified",
      metadata: {},
      onClick: Verifyfunction
    }
  ]);

 function triggerActions(e, row) {
  if (!row.clubDetails || Object.keys(row.clubDetails).length === 0 || !row.clubDetails.name || row.clubDetails.name === "N/A") {
    Swal.fire({
      icon: "warning",
      text: t("Club details are pending"),
      ...SWAL_SETTINGS,
    });
    return;
  }

  const rect = e.currentTarget.getBoundingClientRect();
  const top = rect.top;
  const right = window.innerWidth - rect.right;

  let dynamicOptions = [];

  if (row.isProfileVerified === "verified") {
    if (row.isBlocked) {
      dynamicOptions = [
        {
          label: "Unblock",
          value: false,
          onClick: Blockfunction
        }
      ];
    } else {
      dynamicOptions = [
        {
          label: "Block",
          value: true,
          onClick: Blockfunction
        }
      ];
    }
  } else if (row.isProfileVerified === "rejected") {
    dynamicOptions = [
      {
        label: "Verify",
        value: "verified",
        onClick: Verifyfunction
      }
    ];
  } else {
    // pending case
    dynamicOptions = [
      {
        label: "Reject",
        value: "rejected",
        onClick: Verifyfunction
      },
      {
        label: "Verify",
        value: "verified",
        onClick: Verifyfunction
      }
    ];
  }

  setActionOptions(dynamicOptions);

  setActionState({
    id: "",
    shouldVisible: true,
    positions: { top, right },
    rowData: row
  });
}

  // ================= COLUMNS =================
  const colDefs = useMemo(() => [
    {
      headerName: "",
      checkboxSelection: true,
      headerCheckboxSelection: true,
      headerCheckboxSelectionFilteredOnly: true,
      maxWidth: 45,
      minWidth: 45,
      pinned: "left",
      lockPosition: true,
      suppressMovable: true,
      sortable: false,
      filter: false,
      resizable: false,
    },
    {
      headerName: t("list_heading_sno"),
      field: "srNo",
      maxWidth: 85,
      sortable: false,
      filter: false,
      suppressMovable: true,
      lockPosition: true,
      valueGetter: (params) => {
        if (!params.node || params.node.rowIndex === undefined) return "";
        return params.node.rowIndex + 1;
      },
      cellStyle: { borderRight: "1px solid #dee2e6", color: "#3c4858" },
    },
    {
      headerName: t("label_name"),
      field: "name",
      flex: 1,
      minWidth: 200,
      maxWidth: 450,
    },
    {
      headerName: "Club Name",
      field: "clubname",
      flex: 1,
      minWidth: 150,
      maxWidth: 200,
      sortable: true,
      filter: true,
      tooltipField: "clubname",
      cellRenderer: (params) => (
        <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {params.value || "N/A"}
        </div>
      ),
    },
 

    {
      headerName: "Club Admin Email",
      field: "email",
      flex: 1,
      minWidth: 170,
      maxWidth: 320,
      cellClass: 'ag-cell-ellipsis',
      tooltipField: "email",
      cellRenderer: (params) => {
        return (
          <div
            style={{
              width: "100%",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {params.value || "N/A"}
          </div>
        );
      },
    },
    {
      headerName: "Club Email",
      field: "clubEmail",
      flex: 1,
      minWidth: 170,
      maxWidth: 320,
      cellClass: 'ag-cell-ellipsis',
      tooltipField: "clubEmail",
      cellRenderer: (params) => {
        return (
          <div
            style={{
              width: "100%",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {params.value || "N/A"}
          </div>
        );
      },
    },
    {
      headerName: "Stripe Customer ID",
      field: "stripeCustomerId",
      minWidth: 180,
      maxWidth: 250,
      valueGetter: (params) => params.data.stripeCustomerId || "N/A",
    },
    {
      headerName: "Club Type",
      field: "clubType",
      minWidth: 140,
      maxWidth: 180,
      // cellRenderer: (params) => {
      //   const row = params.data;
      //   return (
      //     <select
      //       className="form-control form-control-sm mt-1"
      //       value={row.clubType || "Founder"}
      //       onChange={(e) => changeClubTypeFunction(row, e.target.value)}
      //     >
      //       <option value="Founder">Founder</option>
      //       <option value="Standard">Standard</option>
      //       <option value="Partner">Partner</option>
      //     </select>
      //   );
      // },
      valueGetter: (params) => params.data.clubType || "Founder",
    },
    {
      headerName: "Tier",
      field: "tier",
      minWidth: 160,
      maxWidth: 220,
      sortable: false,
      filter: false,
      hide: activeTab !== "verified",
      cellRenderer: (params) => {
        const row = params.data;
        const billingStatus = row.billingStatus;

        // ── Free tier — calculate remaining days ──
        const clubType = row.clubType;
        const joinedAt = row.joinedAt || row.createdAt;
        let freeDays = 0;
        if (clubType === "Standard") freeDays = standardFreeDays;
        else if (clubType === "Partner") freeDays = partnerFreeDays;
        else if (clubType === "Founder") freeDays = founderFreeDays;

        // ── Free trial only starts counting after profile is verified ──
        const isVerified = row.isProfileVerified === "verified";

        let remainingDays = null;
        if (isVerified && joinedAt && freeDays > 0) {
          let freeTrialEnd;
          if (row.billingAnchorDate) {
            freeTrialEnd = new Date(row.billingAnchorDate);
          } else {
            const joinedDate = new Date(joinedAt);
            const nominalEndDate = new Date(joinedDate.getTime() + (freeDays + (row.extraFreeDays || 0)) * 24 * 60 * 60 * 1000);
            let anchorMonth = nominalEndDate.getMonth() + 1;
            let anchorYear = nominalEndDate.getFullYear();
            if (anchorMonth > 11) {
              anchorMonth = 0;
              anchorYear += 1;
            }
            freeTrialEnd = new Date(anchorYear, anchorMonth, 1);
          }
          const now = new Date();
          const diff = Math.ceil((freeTrialEnd.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
          remainingDays = diff > 0 ? diff : 0;
        }

        // ── If profile is not verified yet, show nothing ──
        if (!isVerified) {
          return null;
        }

        // ── Auto Billing Failed (past due / payment failed) ──
        if (billingStatus === "past_due") {
          return (
            <div className="d-flex flex-column" style={{ lineHeight: "1.3" }}>
              <div className="d-flex align-items-center">
                <span
                  className="badge badge-pill fw-bold text-uppercase"
                  style={{ background: "#e74c3c", color: "#fff", fontSize: "11px", padding: "5px 10px" }}
                >
                  <i className="ri-error-warning-line me-1"></i>Auto Billing Failed
                </span>
                <button
                  className="btn btn-sm btn-link p-0 ms-2"
                  style={{ color: "#e74c3c", textDecoration: "none", lineHeight: "1", border: "none", background: "none" }}
                  onClick={() => ExtendTrial(row)}
                  title="Add Extra Days"
                >
                  <i className="ri-add-circle-fill" style={{ fontSize: "16px" }}></i>
                </button>
              </div>
            </div>
          );
        }

        // ── If there are remaining free trial days, show Free Tier ──
        if (remainingDays !== null && remainingDays > 0) {
          return (
            <div className="d-flex flex-column" style={{ lineHeight: "1.3" }}>
              <div className="d-flex align-items-center">
                <span
                  className="badge badge-pill fw-bold text-uppercase"
                  style={{ background: "#17a589", color: "#fff", fontSize: "11px", padding: "4px 8px", width: "fit-content" }}
                >
                  <i className="ri-gift-line me-1"></i>Free Tier
                </span>
                <button
                  className="btn btn-sm btn-link p-0 ms-2"
                  style={{ color: "#17a589", textDecoration: "none", lineHeight: "1", border: "none", background: "none" }}
                  onClick={() => ExtendTrial(row)}
                  title="Add Extra Days"
                >
                  <i className="ri-add-circle-fill" style={{ fontSize: "16px" }}></i>
                </button>
              </div>
              <small style={{ color: "#6c757d", fontSize: "10px", marginTop: "2px" }}>
                {remainingDays} day{remainingDays !== 1 ? "s" : ""} remaining
              </small>
            </div>
          );
        }

        // ── If free trial has ended OR status is active/billed/billable, show Auto Billing ──
        if (
          billingStatus === "active" ||
          billingStatus === "billed" ||
          billingStatus === "billable" ||
          (remainingDays !== null && remainingDays <= 0)
        ) {
          return (
            <div className="d-flex flex-column" style={{ lineHeight: "1.3" }}>
              <div className="d-flex align-items-center">
                <span
                  className="badge badge-pill fw-bold text-uppercase"
                  style={{ background: "#6c5ce7", color: "#fff", fontSize: "11px", padding: "4px 8px", width: "fit-content" }}
                >
                  <i className="ri-refresh-line me-1"></i>Auto Billing
                </span>
                <button
                  className="btn btn-sm btn-link p-0 ms-2"
                  style={{ color: "#6c5ce7", textDecoration: "none", lineHeight: "1", border: "none", background: "none" }}
                  onClick={() => ExtendTrial(row)}
                  title="Add Extra Days"
                >
                  <i className="ri-add-circle-fill" style={{ fontSize: "16px" }}></i>
                </button>
              </div>
            </div>
          );
        }

        // ── Fallback (e.g., pending_agreement or setup_pending with no clubType yet) ──
        return (
          <div className="d-flex flex-column" style={{ lineHeight: "1.3" }}>
            <div className="d-flex align-items-center">
              <span
                className="badge badge-pill fw-bold text-uppercase"
                style={{ background: "#17a589", color: "#fff", fontSize: "11px", padding: "4px 8px", width: "fit-content" }}
              >
                <i className="ri-gift-line me-1"></i>Free Tier
              </span>
              <button
                className="btn btn-sm btn-link p-0 ms-2"
                style={{ color: "#17a589", textDecoration: "none", lineHeight: "1", border: "none", background: "none" }}
                onClick={() => ExtendTrial(row)}
                title="Add Extra Days"
              >
                <i className="ri-add-circle-fill" style={{ fontSize: "16px" }}></i>
              </button>
            </div>
            {remainingDays !== null && (
              <small style={{ color: "#6c757d", fontSize: "10px", marginTop: "2px" }}>
                {remainingDays} day{remainingDays !== 1 ? "s" : ""} remaining
              </small>
            )}
          </div>
        );
      },
    },

    {
      headerName: t("label_verification"),
      field: "isProfileVerified",
      minWidth: 140,
      maxWidth: 140,
      sortable: false,
      filter: false,
      filter: true,
      cellStyle: {
        display: "flex",
        justifyContent: "cente",
        alignItems: "center",
      },
      cellRenderer: (params) => {
        const row = params.data;
        return (
          <div className="d-flex ml-3 align-items-center justify-content-between">
            {renderProfileStatus(row)}
            <Whisper
              placement="top"
              trigger="hover"
              speaker={<Tooltip style={{ fontWeight: "bold" }}>Verification status</Tooltip>}
            >
              <button
                type="button"
                className="btn btn-sm p-0 ms-2"
                style={{
                  background: "transparent",
                  border: "none",
                  fontSize: "16px"
                }}
                onClick={(e) => triggerActions(e, row)}
              >
                <i className="ri-settings-3-fill"></i>
              </button>
            </Whisper>
          </div>
        );
      }
    },
    // {
    //   headerName: t("label_phone_number"),
    //   field: "phoneNumber",
    //   minWidth: 150,
    // },
    {
      headerName: t("list_heading_created_date"),
      field: "createdAt",
      filter: "agDateColumnFilter",
      minWidth: 140,
      maxWidth: 140,
      valueGetter: (params) =>
        params.data?.createdAt ? new Date(params.data.createdAt) : null,
      valueFormatter: (params) =>
        params.value ? moment(params.value).format("MMMM DD, YYYY") : ""
    },
    ...(hasP("club-admin-status") ? [{
      headerName: t("list_heading_status"),
      field: "status",
      filter: true,
      maxWidth: 110,
      cellStyle: {
        display: "flex",
        justifyContent: "center"
      },
      cellRenderer: (params) => {
        const currentStatus = params.value;
        const isActive = currentStatus === STATUS.ACTIVE;

        return (
          <label className={`custom-switch cp${!isActive ? " e_none" : ""}`}>
            <input
              type="checkbox"
              className="custom-switch-input cp"
              checked={isActive}
              onChange={() => {
                const newStatus = isActive ? STATUS.INACTIVE : STATUS.ACTIVE;
                ChangeStatus(params.data.originalId, newStatus);
              }}
            />
            <span className="custom-switch-indicator cp"></span>
          </label>
        );
      },
    }] : []),
    ...(hasP("club-admin-view") || hasP("club-admin-edit") || hasP("club-admin-delete") || hasP("club-admin-verify") ? [{
      headerName: t("list_heading_action"),
      minWidth: 220,
      maxWidth: 250,
      pinned: "right",
      sortable: false,
      filter: false,
      cellRenderer: (params) => {
        const row = params.data;
        const isPending = row.isProfileVerified === "pending" || row.isProfileVerified === "rejected";
        return (
          <div className="btn-toolbar table-actions">
            <div className="btn-group btn-group-sm">
              {hasP("club-admin-view") && (
                <>
                  <Whisper
                    placement="top"
                    trigger="hover"
                    speaker={<Tooltip style={{ fontWeight: 'bold' }}>View</Tooltip>}
                  >
                    <button
                      type="button"
                      className="btn view"
                      onClick={() => viewfunction(row)}
                    >
                      <i className="ri-eye-fill"></i>
                    </button>
                  </Whisper>
                  {!isPending && (
                    <Whisper
                      placement="top"
                      trigger="hover"
                      speaker={<Tooltip style={{ fontWeight: 'bold' }}>Audit Logs</Tooltip>}
                    >
                      <button
                        type="button"
                        className="btn view ml-2"
                        style={{ backgroundColor: '#10b981', color: '#fff' }}
                        onClick={() => navigate(`/admin/user-management/audit-logs/${row.originalId}`)}
                      >
                        <i className="ri-history-fill"></i>
                      </button>
                    </Whisper>
                  )}
                </>
              )}

              {!isPending && hasP("club-admin-edit") && (
                <Whisper
                  placement="top"
                  trigger="hover"
                  speaker={<Tooltip style={{ fontWeight: 'bold' }}>Edit</Tooltip>}
                >
                  <button
                    type="button"
                    className="btn edit"
                    onClick={() => Editfunction(row)}
                  >
                    <i className="ri-pencil-fill"></i>
                  </button>
                </Whisper>
              )}

              {!isPending && hasP("club-admin-delete") && (
                <Whisper
                  placement="top"
                  trigger="hover"
                  speaker={<Tooltip style={{ fontWeight: 'bold' }}>Delete</Tooltip>}
                >
                  <button
                    type="button"
                    className="btn delete"
                    onClick={() => Deletefunction(row)}
                  >
                    <i className="ri-delete-bin-6-fill"></i>
                  </button>
                </Whisper>
              )}

              {!isPending && (
                <Whisper
                  placement="top"
                  trigger="hover"
                  speaker={<Tooltip style={{ fontWeight: 'bold' }}>Export PDF</Tooltip>}
                >
                  <button
                    type="button"
                    className="btn delete"
                    style={{ color: '#e17055' }}
                    onClick={() => downloadPDF(row)}
                  >
                    <i className="ri-file-pdf-2-fill"></i>
                  </button>
                </Whisper>
              )}

              <Whisper
                placement="top"
                trigger="hover"
                speaker={<Tooltip style={{ fontWeight: 'bold' }}>Send Invitation</Tooltip>}
              >
                <button
                  type="button"
                  className="btn edit"
                  style={{ color: '#e17055' }}
                  onClick={() => ResendInvite(row)}
                >
                  <i className="ri-mail-send-fill"></i>
                </button>
              </Whisper>

              {/* {!isPending && (
                <Whisper
                  placement="top"
                  trigger="hover"
                  speaker={<Tooltip style={{ fontWeight: 'bold' }}>Transfer Role</Tooltip>}
                >
                  <button
                    type="button"
                    className="btn edit"
                    style={{ color: '#6c5ce7' }}
                    onClick={() => transferRoleFunction(row)}
                  >
                    <i className="ri-arrow-left-right-line"></i>
                  </button>
                </Whisper>
              )} */}
{/* 
              <Whisper
                placement="top"
                trigger="hover"
                speaker={<Tooltip style={{ fontWeight: 'bold' }}>Auto Payment Deduct</Tooltip>}
              >
                <button
                  type="button"
                  className="btn edit"
                  style={{ color: '#2ecc71' }}
                  onClick={() => handleAutoPaymentDeduct(row)}
                >
                  <i className="ri-bank-card-fill"></i>
                </button>
              </Whisper> */}

              {/* {row.isProfileVerified === "pending" && hasP("club-admin-verify") && (
                <Whisper
                  placement="top"
                  trigger="hover"
                  speaker={<Tooltip style={{ fontWeight: 'bold' }}>More</Tooltip>}
                >
                  <div
                    type="button"
                    className="btn more"
                    id={`dropdownMenuButton${row.originalId}`}
                    data-bs-toggle="dropdown"
                    aria-expanded="false"
                  >
                    <div className="dropdown">
                      <i className="ri-more-fill"></i>
                      <ul                      
                        className="dropdown-menu" aria-labelledby={`dropdownMenuButton${row.originalId}`}>
                        <li>
                          <button
                            className="dropdown-item"
                            type="button"
                            onClick={() => Verifyfunction(row, "rejected")}
                          >
                            Reject
                          </button>
                        </li>
                        <li>
                          <button
                            className="dropdown-item"
                            type="button"
                            onClick={() => Verifyfunction(row, "verified")}
                          >
                            Verify
                          </button>
                        </li>
                      </ul>
                    </div>
                  </div>
                </Whisper>
              )} */}

            </div>
          </div>
        );
      },
    }] : []),
  ], [t, navigate, activeTab]);

  const rowData = useMemo(() => {
    if (!data?.data?.list) return [];

    return data.data.list
      .map((row, i) => ({
        srNo: 0, // 🔥 Don't set manually - let valueGetter handle
        originalId: row.id,
        name: `${row?.firstName ? TrimText(row.firstName, 10) : "N/A"} ${row?.lastName ? TrimText(row.lastName, 10) : ""}`.trim(),
        rawFirstName: row?.firstName || "",
        rawLastName: row?.lastName || "",
        username: row?.username || "",
        dob: row?.dob || "",
        city: row?.city || "",
        state: row?.state || "",
        country: row?.country || "",
        clubname: row?.clubDetails?.name || "",
        email: row?.email || "N/A",
        clubEmail: row?.clubDetails?.email || row?.clubEmail || "N/A",

        isProfileVerified: row?.isProfileVerified || "pending",
        isBlocked: row?.isBlocked || false,
        clubDetails: row?.clubDetails || {},
        stripeCustomerId: row?.stripeCustomerId,
        clubType: row?.clubType,
        billingStatus: row?.billingStatus || null,
        billingAnchorDate: row?.billingAnchorDate || null,
        extraFreeDays: row?.extraFreeDays || 0,
        joinedAt: row?.joinedAt || null,
        // phoneNumber: row?.phone_number ? TrimText(row.phone_number, 20) : "N/A",
        status: row.status,
        createdAt: row.createdAt,
      }))
      .filter((row) => {
        // Tab Filtering
        if (activeTab === "verified") {
          if (row.isProfileVerified !== "verified" || row.isBlocked) return false;
        } else if (activeTab === "pending") {
          if (row.isProfileVerified !== "pending" || row.isBlocked) return false;
        } else if (activeTab === "rejected_blocked") {
          if (row.isProfileVerified !== "rejected" && !row.isBlocked) return false;
        }

        // Search Filtering
        return searchData
          ? row.name.toLowerCase().includes(searchData.toLowerCase()) ||
          row.email.toLowerCase().includes(searchData.toLowerCase()) ||
          row.clubEmail.toLowerCase().includes(searchData.toLowerCase()) ||
          (row.username && row.username.toLowerCase().includes(searchData.toLowerCase())) ||
          (row.clubname && row.clubname.toLowerCase().includes(searchData.toLowerCase()))
          : true;
      });
  }, [data, searchData, activeTab]); // 🔥 Removed pageIndex, perPage

  // ================= AG GRID CONFIG =================
  const getAgGridConfig = useMemo(() => ({
    ref: gridRef,
    domLayout: "autoHeight",
    rowData: rowData,
    columnDefs: colDefs,
    getRowId: (params) => String(params.data.originalId),
    noRowsOverlayComponent: NoRowsOverlay,
    pagination: true,
    paginationPageSize: perPage,
    paginationPageSizeSelector: [10, 20, 50, 100],
    suppressCellFocus: true,
    defaultColDef: {
      resizable: true,
      sortable: true,
      minWidth: 150,
      cellStyle: {
        padding: "10px 8px",
      },
      suppressHeaderMenuButton: true,
      suppressHeaderFilterButton: true,
    },
    rowSelection: "multiple",
    suppressRowClickSelection: true,
    onSelectionChanged: onSelectionChanged,
    headerHeight: 50,
    rowHeight: 45,
    onPaginationChanged: (params) => {
      if (params.api) {
        const currentPage = params.api.paginationGetCurrentPage();
        const pageSize = params.api.paginationGetPageSize();
        setPerPage(pageSize);
      }
    },
  }), [rowData, colDefs, perPage]);

  return (
    <>
      <Breadcrums data={breadcrumbs} />
      <div className="animation_fade">
        <div className="card custom-card overflow-hidden">
          <div className="card-body">
            <ul className="nav nav-tabs mb-4">
              <li className="nav-item">
                <button
                  className={`nav-link ${activeTab === "verified" ? "active fw-bold" : ""}`}
                  type="button"
                  onClick={() => setActiveTab("verified")}
                >
                  Club Admins
                </button>
              </li>
              <li className="nav-item">
                <button
                  className={`nav-link ${activeTab === "pending" ? "active fw-bold" : ""}`}
                  type="button"
                  onClick={() => setActiveTab("pending")}
                >
                  Pending Requests
                </button>
              </li>
              <li className="nav-item">
                <button
                  className={`nav-link ${activeTab === "rejected_blocked" ? "active fw-bold" : ""}`}
                  type="button"
                  onClick={() => setActiveTab("rejected_blocked")}
                >
                  Rejected / Blocked
                </button>
              </li>
            </ul>
            <div className="d-flex justify-content-between align-items-center mb-4">
              <div className="d-flex align-items-center gap-3">
                <h6 className="main-content-label mb-0">Club</h6>
                {hasP("club-admin-delete") && selectedRows.length > 0 && (
                  <button
                    type="button"
                    className="btn btn-danger btn-sm d-flex align-items-center gap-1"
                    onClick={handleDeleteMultiple}
                  >
                    <i className="ri-delete-bin-line"></i> Delete Selected ({selectedRows.length})
                  </button>
                )}
              </div>
              <div className="d-flex align-items-center">
                <div style={{ width: 300 }} className="me-3">
                  <input
                    type="text"
                    className="form-control"
                    placeholder={t("input_placeholder_search")}
                    value={searchData}
                    onChange={(e) => setSearchData(e.target.value)}
                    style={{ outline: "none", boxShadow: "none" }}
                  />
                </div>

                {/* status */}
                <select
                  className="form-select form-control me-3 cp"
                  style={{ width: 100 }}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (gridRef?.current && gridRef?.current?.api) {
                      if (value && value.length) {
                        gridRef?.current?.api.setFilterModel({
                          status: {
                            type: "equals",
                            filter: value
                          }
                        });
                      } else {
                        gridRef?.current?.api?.setFilterModel(null);
                      }
                      gridRef?.current?.api?.onFilterChanged();
                    }
                  }}
                >
                  <option value="">{t("list_heading_status")}</option>
                  <option value={STATUS.ACTIVE}>{t("btn_active")}</option>
                  <option value={STATUS.INACTIVE}>{t("btn_inactive")}</option>
                </select>

                {/* date */}
                <div className="me-3">
                  <CustomProvider>
                    <DateRangePicker
                      style={{ width: "150px" }}
                      placeholder={t("input_placeholder_date")}
                      placement="bottomEnd"
                      onChange={(value) => {
                        if (!gridRef?.current || !gridRef?.current?.api) return;

                        if (!value || value?.length !== 2) {
                          gridRef?.current?.api?.setFilterModel(null);
                          gridRef?.current?.api?.onFilterChanged();
                          return;
                        }
                        const [start, end] = value;
                        gridRef?.current?.api?.setFilterModel({
                          createdAt: {
                            type: "inRange",
                            dateFrom: moment(start).startOf("day").format("YYYY-MM-DD HH:mm:ss"),
                            dateTo: moment(end).endOf("day").format("YYYY-MM-DD HH:mm:ss")
                          }
                        })
                        gridRef?.current?.api?.onFilterChanged();
                      }}
                    />
                  </CustomProvider>
                </div>
              </div>
            </div>

            {isLoading ? (
              <Placeholder.Paragraph
                className="w-100 admin-placeholder"
                rowHeight={30}
                rowSpacing={5}
                active={true}
                rows={7}
              />
            ) : (
              <div className="ag-theme-alpine" style={{ width: '100%' }}>
                <AgGridReact {...getAgGridConfig} />
              </div>
            )}
          </div>
        </div>
      </div>
      <ActionCell data={actionState} onClose={() => setActionState(null)} options={actionOptions} />
    </>
  );
};

export default ClubAdminList;

